import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ClickAwayListener } from '@mui/material';
import { Tooltip } from './Tooltip.component';

function useEventListener(eventName, handler, element = window) {
  const savedHandler = useRef();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;
    const eventListener = (event) => savedHandler.current(event);
    element.addEventListener(eventName, eventListener, true);
    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener(eventName, eventListener, true);
    };
  }, [eventName, element]);
}

/**
* Renders a <TooltipClickable /> component.
* Wrapper for designSystem Tooltip, but manually controlling show/hide. Closes automatically when clicking away.
* @param {Object} props
* @param {bool} props.show - value used to set visibility, usually a state variable.
* @param {func} props.setShow - function used to change show value, usually a state variable setter. Used by ClickAwayListener.
* @param {node | node[]} props.children - Components which will be wrapped by the tooltip
* @param {node} props.title - Text for the tooltip
* @param {string} props.variant - light or dark
*/
export const TooltipClickable = ({
  show,
  setShow,
  children,
  ...rest
}) => {
  const handler = useCallback((e) => {
    if (setShow && (e.key === 'Escape')) {
      setShow(false);
    }
  }, []);

  const scrollHandler = useCallback(() => {
    if (setShow) {
      setShow(false);
    }
  }, [setShow]);

  useEventListener('keydown', handler);
  useEventListener('scroll', scrollHandler);

  return (show ? (
    <ClickAwayListener
      disableReactTree
      onClickAway={() => setShow(false)}
    >
      <span>
        <Tooltip
          open={show}
          PopperProps={{ disablePortal: false }}
          disableHoverListener
          {...rest}
        >
          {children}
        </Tooltip>
      </span>
    </ClickAwayListener>
  ) : (<>{children}</>));
};

TooltipClickable.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
